import { useCallback } from 'react';
import baseLoadable, {
  DefaultComponent,
  Options,
  LoadableComponent,
} from '@loadable/component';

import { useAppContext } from '@contexts/AppContext';
import RouterPreloader from '@components/RouterPreloader';

import { useIsClient } from './common';

type LoadableFunc = <T>(
  loadFn: (props: T) => Promise<DefaultComponent<T>>,
  options?: Options<T>
) => LoadableComponent<T>;

export const loadableWithFallback: LoadableFunc = (loadFn, options) => (
  baseLoadable(
    loadFn,
    {
      fallback: <RouterPreloader />,
      ...options,
    },
  )
);

export function withClientOnly<P>(Component: React.ComponentType<P>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, react/display-name
  return (props: any) => {
    const isClient = useIsClient();

    return isClient ? <Component {...props} /> : null;
  };
}

export const useLoadableWithBot = () => {
  const { isBot } = useAppContext();
  
  const loadable = useCallback<typeof loadableWithFallback>(
    (loadFn, options) => loadableWithFallback(
      loadFn,
      {
        resolveComponent: (module) => {
          if (!('default' in module)) {
            throw new Error('Module should have default export');
          }
  
          const component = module.default;
  
          if (options?.ssr && isBot) {
            return component;
          }
  
          return withClientOnly(component);
        },
        ...options,
        ssr: true,
      },
    ),
    [isBot],
  );

  return loadable;
};
